<template>
  <div>
    <v-container>
      <grid
        :items="cities"
        :headers="cityHeaders"
        type="cities"
        :loading="loading"
        @addCity="openAdd"
        @updateCity="openEdit"
        @removeCity="removeCity"
      />
    </v-container>

    <!-- One dialog for add and edit: it always opens with the city it is for (or empty). -->
    <v-dialog v-model="dialog" max-width="560" scrollable :persistent="saving">
      <v-card>
        <v-card-title class="text-h6">{{ isEdit ? 'Edit city' : 'Add city' }}</v-card-title>
        <v-card-text class="pt-2">
          <v-form ref="form" @submit.prevent="save">
            <div class="tn-form-section">City</div>
            <v-text-field
              v-model="city.name"
              label="Name"
              outlined
              counter="32"
              :rules="[v => !!(v && v.trim()) || 'Name is required', v => !v || (v.trim().length >= 3 && v.trim().length <= 32) || '3 to 32 characters']"
            ></v-text-field>
            <v-textarea
              v-model="city.description"
              label="Description"
              hint="Include the city name in the description"
              outlined
              rows="3"
              auto-grow
            ></v-textarea>
            <v-autocomplete
              v-model="airport"
              :loading="airportLoading"
              :items="airports"
              :search-input.sync="airportSearch"
              label="Airport"
              placeholder="Search by airport name"
              prepend-inner-icon="mdi-airport"
              item-text="Name"
              item-value="Code"
              hide-no-data
              clearable
              no-filter
              outlined
            >
              <template v-slot:item="data">
                <v-list-item-content>
                  <v-list-item-title>{{ data.item.Name }}</v-list-item-title>
                  <v-list-item-subtitle>{{ data.item.Code }}</v-list-item-subtitle>
                </v-list-item-content>
              </template>
              <template v-slot:selection="data">
                <span>{{ data.item.Name }} ({{ data.item.Code }})</span>
              </template>
            </v-autocomplete>

            <image-field v-model="city.image" :current="current.image" label="City image" />
            <v-text-field v-model="city.caption" label="Image caption" outlined></v-text-field>
            <v-text-field v-model="city.alt" label="Image alt text" outlined></v-text-field>

            <div class="tn-form-section">SEO</div>
            <v-text-field
              v-model="city.seo_title"
              label="SEO title"
              outlined
              counter="500"
              :rules="[v => !v || v.length <= 500 || 'At most 500 characters']"
            ></v-text-field>
            <v-textarea
              v-model="city.seo_description"
              label="SEO description"
              outlined
              rows="3"
              auto-grow
              counter="500"
              :rules="[v => !v || v.length <= 500 || 'At most 500 characters']"
            ></v-textarea>
            <v-text-field v-if="isEdit" v-model="city.slug" label="SEO slug" outlined></v-text-field>
            <image-field v-model="city.featured_image" :current="current.featured_image" label="SEO image" />
          </v-form>
        </v-card-text>
        <v-card-actions class="px-6 pb-5">
          <v-btn text :disabled="saving" @click="dialog = false">Cancel</v-btn>
          <v-spacer></v-spacer>
          <v-btn depressed color="primary" :loading="saving" @click="save">{{ isEdit ? 'Save changes' : 'Add city' }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { tripCities, tripCity, getAirports, headers } from '../../links'
import grid from '../../components/tables.vue'
import ImageField from '../../components/ImageField.vue'
import Swal from 'sweetalert2'

function emptyCity () {
  return { name: '', description: '', image: null, caption: '', alt: '', seo_title: '', seo_description: '', slug: '', featured_image: null }
}

// Older saves stored the text "null" for empty fields.
function text (value) {
  return value === null || value === undefined || value === 'null' ? '' : String(value)
}

export default {
  components: {
    grid,
    ImageField
  },
  data () {
    return {
      cities: [],
      loading: true,
      cityHeaders: [
        { text: 'Image', value: 'image_url', sortable: false },
        { text: 'City', value: 'name' },
        { text: 'Airport', value: 'airport_code' },
        { text: 'Slug', value: 'slug' },
        { text: 'Edit', value: 'editCity' },
        { text: 'Remove', value: 'removeCityById', align: 'end' }
      ],
      dialog: false,
      isEdit: false,
      id: null,
      city: emptyCity(),
      current: { image: null, featured_image: null }, // URLs of the saved images (edit)
      airport: null,
      airports: [],
      airportSearch: null,
      airportLoading: false,
      airportTimer: null,
      saving: false
    }
  },
  watch: {
    airportSearch (value) {
      const selected = this.airports.find(item => item.Code === this.airport)
      if (!value || (selected && selected.Name === value)) return
      clearTimeout(this.airportTimer)
      this.airportTimer = setTimeout(() => this.findAirports(value), 300)
    }
  },
  methods: {
    notify (color, text) {
      this.$root.$emit('tn-notify', { color, text })
    },
    // Every open starts from a clean form: nothing is left over from the previous city.
    open (isEdit, values, id, current, airport) {
      this.isEdit = isEdit
      this.id = id
      this.city = { ...emptyCity(), ...values }
      this.current = current
      this.airports = airport ? [airport] : []
      this.airport = airport ? airport.Code : null
      this.airportSearch = null
      this.dialog = true
      this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation())
    },
    openAdd () {
      this.open(false, {}, null, { image: null, featured_image: null }, null)
    },
    openEdit (item) {
      const code = text(item.airport_code)
      this.open(true, {
        name: text(item.name),
        description: text(item.description),
        caption: text(item.image_caption),
        alt: text(item.image_alt),
        seo_title: text(item.seo_title),
        seo_description: text(item.seo_description),
        slug: text(item.slug)
      }, item.id, { image: item.image_url || null, featured_image: item.featured_image_url || null },
      code ? { Code: code, Name: item.airport_name || code } : null)
    },
    findAirports (term) {
      this.airportLoading = true
      this.$http.get(getAirports(encodeURIComponent(term)), { headers: headers(this.$cookies.get('userToken')) }).then(response => {
        const selected = this.airports.find(item => item.Code === this.airport)
        const found = response.body || []
        // keep the chosen airport in the list so it stays displayed
        this.airports = selected && !found.some(item => item.Code === selected.Code) ? [selected, ...found] : found
      }).finally(() => {
        this.airportLoading = false
      })
    },
    save () {
      if (!this.$refs.form.validate()) return
      const formData = new FormData()
      if (this.isEdit) formData.append('_method', 'PUT')
      formData.append('name', this.city.name.trim())
      formData.append('description', this.city.description.trim())
      formData.append('airportCode', this.airport || '')
      formData.append('image_alt', this.city.alt.trim())
      formData.append('image_caption', this.city.caption.trim())
      formData.append('seo_title', this.city.seo_title.trim())
      formData.append('seo_description', this.city.seo_description.trim())
      if (this.isEdit) formData.append('slug', this.city.slug.trim())
      if (this.city.image) formData.append('image', this.city.image, this.city.image.name)
      if (this.city.featured_image) formData.append('featured_image', this.city.featured_image, this.city.featured_image.name)

      this.saving = true
      const url = this.isEdit ? tripCity(this.id) : tripCities
      this.$http.post(url, formData, { headers: headers(this.$cookies.get('userToken')) }).then(() => {
        this.saving = false
        this.dialog = false
        this.notify('success', this.isEdit ? 'City saved' : 'City added')
        this.getCities()
      }, err => {
        this.saving = false
        this.notify('error', this.errorText(err, 'Could not save the city'))
      })
    },
    // 422 bodies: { errors: { field: [message] } } or { errors: [message] }
    errorText (err, fallback) {
      const body = (err && err.body) || {}
      const errors = body.errors && (Array.isArray(body.errors) ? body.errors : Object.values(body.errors))
      if (errors && errors.length) return [].concat(errors[0])[0]
      return body.message || fallback
    },
    removeCity (id) {
      const city = this.cities.find(item => item.id === id)
      Swal.fire({
        title: `Delete ${city ? city.name : 'this city'}?`,
        text: 'This can\'t be undone.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Delete',
        cancelButtonText: 'Keep it'
      }).then(result => {
        if (!result.isConfirmed) return
        this.$http.delete(tripCity(id), { headers: headers(this.$cookies.get('userToken')) }).then(() => {
          this.notify('success', 'City deleted')
          this.getCities()
        }, err => {
          this.notify('error', this.errorText(err, 'Could not delete the city'))
        })
      })
    },
    getCities () {
      this.loading = true
      this.$http.get(tripCities, { headers: headers(this.$cookies.get('userToken')) }).then(response => {
        this.loading = false
        this.cities = response.body.cities || []
      }, err => {
        this.loading = false
        this.notify('error', this.errorText(err, 'Could not load the cities'))
      })
    }
  },
  created () {
    this.getCities()
  },
  beforeDestroy () {
    clearTimeout(this.airportTimer)
  }
}
</script>

<style lang="scss" scoped>
.tn-form-section {
  margin: 4px 0 12px;
  color: var(--tn-muted);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: .06em;
  text-transform: uppercase;
}
</style>
